export type MetadataStandard = "dublin_core" | "marc" | "iptc" | "custom";

export type MetadataFieldSource = "catalog" | "custom";

export type KnownMetadataFieldKey =
  | "title"
  | "creator"
  | "subject"
  | "description"
  | "publisher"
  | "contributor"
  | "type"
  | "format"
  | "identifier"
  | "source"
  | "language"
  | "relation"
  | "coverage"
  | "rights"
  | "date"
  | "participants"
  | "tags"
  | "location";

/** Locked base fields always present; fixed order across Settings, Conclude, and Upload. */
export const LOCKED_BASE_METADATA_FIELD_KEYS = [
  "title",
  "description",
  "date",
  "participants",
  "tags",
] as const satisfies readonly KnownMetadataFieldKey[];

export const BASE_FIELD_ORDER_MAP: Record<string, number> = {
  title: 0,
  description: 1,
  date: 2,
  participants: 3,
  tags: 4,
};

export function isLockedBaseMetadataFieldKey(key: string): boolean {
  return (LOCKED_BASE_METADATA_FIELD_KEYS as readonly string[]).includes(key);
}

export type MetadataFieldKey = KnownMetadataFieldKey | string;

export interface MetadataCatalogEntry {
  key: KnownMetadataFieldKey;
  label: string;
  description: string;
}

export type MetadataAnswerType =
  | "Text"
  | "Numeric"
  | "Date"
  | "Multi-Select"
  | "Dropdown List"
  | "Radio Button"
  | "Descriptive Text";

export interface ProjectMetadataFieldConfig {
  key: MetadataFieldKey;
  label: string;
  enabled: boolean;
  order: number;
  source: MetadataFieldSource;
  answerType: MetadataAnswerType;
  required: boolean;
  showOnConclude?: boolean;
  showOnUpload?: boolean;
  options?: string[];
  description?: string;
  deleted?: boolean;
}

export interface ProjectMetadataSchema {
  version: 1;
  standard?: MetadataStandard;
  showEmptyFields: boolean;
  fields: ProjectMetadataFieldConfig[];
}

export type StoryMetadataValue = string | string[] | null;

export type StoryMetadataValues = Record<string, StoryMetadataValue>;

/** Where a Fields Library entry was found. */
export type FieldsLibrarySource = "project_metadata" | "testimonials";

/**
 * A deduplicated custom field found across the organization's projects metadata schemas,
 * offered in the "Fields Library" so the same field isn't recreated multiple times.
 */
export interface FieldsLibraryEntry {
  label: string;
  answerType: MetadataAnswerType;
  options?: string[];
  sources: FieldsLibrarySource[];
  usageCount: number;
}

export const CORE_METADATA_FIELDS: MetadataCatalogEntry[] = [
  {
    key: "title",
    label: "Title",
    description: "Name of the asset",
  },
  {
    key: "description",
    label: "Description",
    description: "Summary",
  },
  {
    key: "publisher",
    label: "Publisher",
    description: "The entity responsible for publishing this resource",
  },
  {
    key: "type",
    label: "Type",
    description: "The nature or genre of the resource",
  },
  {
    key: "format",
    label: "Format",
    description: "The file format or physical medium",
  },
  {
    key: "identifier",
    label: "Identifier",
    description: "A unique identifier such as a URL, DOI, or ISBN",
  },
  {
    key: "source",
    label: "Source",
    description: "The related resource from which this is derived",
  },
  {
    key: "language",
    label: "Language",
    description: "The language of the resource's content",
  },
  {
    key: "relation",
    label: "Relation",
    description: "A related resource",
  },
  {
    key: "coverage",
    label: "Coverage",
    description: "The spatial or temporal scope of the content",
  },
  {
    key: "rights",
    label: "Rights",
    description: "Copyright or licensing information",
  },
];

export const LEGACY_METADATA_FIELDS: MetadataCatalogEntry[] = [
  {
    key: "date",
    label: "Date Recorded",
    description: "The date this recording or interview took place.",
  },
  {
    key: "participants",
    label: "Participants",
    description: "People who took part in the interview.",
  },
  {
    key: "tags",
    label: "Keywords",
    description: "Keywords or phrases that describe the content.",
  },
  {
    key: "location",
    label: "Location",
    description: "The spatial or temporal topic of the interview.",
  },
];

/**
 * Reserved keys kept for validation / old StoryMetadata values.
 * Not offered in Settings or Dublin Core apply (Creator stays Hub header-only).
 */
export const RETIRED_METADATA_FIELDS: MetadataCatalogEntry[] = [
  {
    key: "creator",
    label: "Creator",
    description: "The person or entity who created this recording",
  },
];

export const ALL_KNOWN_FIELDS = [...CORE_METADATA_FIELDS, ...LEGACY_METADATA_FIELDS, ...RETIRED_METADATA_FIELDS];

/** Dublin Core catalog fields offered in the product schema UI. */
export const DUBLIN_CORE_CATALOG = CORE_METADATA_FIELDS;

const KNOWN_METADATA_FIELD_KEY_SET = new Set<string>(ALL_KNOWN_FIELDS.map((entry) => entry.key));

const METADATA_STANDARDS: MetadataStandard[] = ["dublin_core", "marc", "iptc", "custom"];

const CUSTOM_METADATA_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

function fieldConfigFromCatalogEntry(
  entry: MetadataCatalogEntry,
  order: number,
  enabled: boolean
): ProjectMetadataFieldConfig {
  const key = entry.key;

  let answerType: MetadataAnswerType = "Text";
  if (key === "date") {
    answerType = "Date";
  } else if (key === "participants" || key === "tags" || key === "subject" || key === "contributor") {
    answerType = "Multi-Select";
  }

  return {
    key,
    label: entry.label,
    enabled,
    order,
    source: "catalog",
    answerType,
    required: key === "title",
    options: [],
    description: entry.description || "",
  };
}

export function isLockedBaseMetadataFieldRequired(key: string): boolean {
  return key === "title";
}

export function getDefaultProjectMetadataSchema(): ProjectMetadataSchema {
  return getLegacyProjectMetadataSchema();
}

export function getLegacyProjectMetadataSchema(): ProjectMetadataSchema {
  const catalogMap = new Map(ALL_KNOWN_FIELDS.map((field) => [field.key, field]));
  return {
    version: 1,
    standard: "custom",
    showEmptyFields: false,
    fields: LOCKED_BASE_METADATA_FIELD_KEYS.map((key, index) => {
      const entry = catalogMap.get(key);
      if (!entry) {
        throw new Error(`Legacy field ${key} not found in catalog`);
      }
      return fieldConfigFromCatalogEntry(entry, index, true);
    }),
  };
}

function sortFieldsByBaseOrder(fields: ProjectMetadataFieldConfig[]): ProjectMetadataFieldConfig[] {
  const sorted = [...fields].sort((a, b) => {
    const aBase = BASE_FIELD_ORDER_MAP[a.key] ?? -1;
    const bBase = BASE_FIELD_ORDER_MAP[b.key] ?? -1;
    if (aBase !== -1 && bBase !== -1) return aBase - bBase;
    if (aBase !== -1) return -1;
    if (bBase !== -1) return 1;
    return a.order - b.order;
  });
  return sorted.map((field, index) => ({ ...field, order: index }));
}

function fallbackAnswerTypeForFieldKey(key: string): MetadataAnswerType {
  if (key === "date") {
    return "Date";
  }
  if (key === "participants" || key === "tags" || key === "subject" || key === "contributor") {
    return "Multi-Select";
  }
  return "Text";
}

/** Ensures all locked base fields exist (e.g. after schema changes on legacy projects). */
export function ensureBaseMetadataFields(schema: ProjectMetadataSchema): ProjectMetadataSchema {
  const legacySchema = getLegacyProjectMetadataSchema();
  const legacyMap = new Map(legacySchema.fields.map((field) => [field.key, field]));

  const healedFields = (schema.fields || []).map((field) => {
    let healed = field;

    const isKnownCatalogKey = KNOWN_METADATA_FIELD_KEY_SET.has(field.key.trim());
    const source: MetadataFieldSource = isKnownCatalogKey ? "catalog" : "custom";
    healed = { ...healed, source };

    // Creator is Hub header-only (story author); not an editable schema field.
    if (field.key === "creator") {
      healed = { ...healed, deleted: true, enabled: false };
    }
    if (field.key === "date" && (field.label === "Date" || !field.label?.trim())) {
      healed = { ...healed, label: "Date Recorded" };
    }
    if (field.key === "tags" && field.label === "Tags") {
      healed = { ...healed, label: "Keywords" };
    }
    if (!field.answerType) {
      healed = { ...healed, answerType: fallbackAnswerTypeForFieldKey(field.key) };
    }
    if (typeof healed.required !== "boolean") {
      healed = { ...healed, required: healed.key === "title" };
    }
    if (!Array.isArray(healed.options)) {
      healed = { ...healed, options: [] };
    } else {
      healed = {
        ...healed,
        options: healed.options.filter((entry) => typeof entry === "string"),
      };
    }
    if (isLockedBaseMetadataFieldKey(field.key)) {
      healed = {
        ...healed,
        enabled: true,
      };
    }
    return healed;
  });

  const missingFields: ProjectMetadataFieldConfig[] = [];
  for (const key of LOCKED_BASE_METADATA_FIELD_KEYS) {
    const exists = healedFields.some((field) => field.key === key);
    if (!exists) {
      const canonicalField = legacyMap.get(key);
      if (canonicalField) {
        missingFields.push(canonicalField);
      }
    }
  }

  return {
    ...schema,
    fields: sortFieldsByBaseOrder([...healedFields, ...missingFields]),
  };
}

export function createProjectMetadataSchemaFromCatalog(
  catalog: MetadataCatalogEntry[],
  options?: { standard?: MetadataStandard; showEmptyFields?: boolean; enabled?: boolean }
): ProjectMetadataSchema {
  const enabled = options?.enabled ?? true;

  return {
    version: 1,
    standard: options?.standard ?? "dublin_core",
    showEmptyFields: options?.showEmptyFields ?? false,
    fields: catalog.map((entry, index) => fieldConfigFromCatalogEntry(entry, index, enabled)),
  };
}

export function applyMetadataStandardTemplate(standard: MetadataStandard): ProjectMetadataSchema | null {
  if (standard === "dublin_core") {
    const baseFields = getLegacyProjectMetadataSchema().fields;

    const remainingCatalog = CORE_METADATA_FIELDS.filter(
      (entry) => entry.key !== "title" && entry.key !== "description"
    );

    const extraFields = remainingCatalog.map((entry, index) =>
      fieldConfigFromCatalogEntry(entry, baseFields.length + index, true)
    );

    return {
      version: 1,
      standard: "dublin_core",
      showEmptyFields: false,
      fields: [...baseFields, ...extraFields],
    };
  }

  return null;
}

export function normalizeProjectMetadataSchema(schema: ProjectMetadataSchema): ProjectMetadataSchema {
  return {
    version: 1,
    standard: schema.standard,
    showEmptyFields: schema.showEmptyFields,
    fields: [...schema.fields]
      .map((field) => {
        const trimmedKey = field.key.trim();
        const isKnownCatalogKey = KNOWN_METADATA_FIELD_KEY_SET.has(trimmedKey);
        const source: MetadataFieldSource = isKnownCatalogKey ? "catalog" : "custom";

        return {
          ...field,
          key: trimmedKey,
          label: field.label.trim(),
          source,
          answerType: field.answerType,
          required: field.required,
          options: Array.isArray(field.options) ? field.options.filter((entry) => typeof entry === "string") : [],
          description: field.description ?? undefined,
          deleted: field.deleted ?? undefined,
        };
      })
      .sort((a, b) => {
        const aBase = BASE_FIELD_ORDER_MAP[a.key] ?? -1;
        const bBase = BASE_FIELD_ORDER_MAP[b.key] ?? -1;
        if (aBase !== -1 && bBase !== -1) return aBase - bBase;
        if (aBase !== -1) return -1;
        if (bBase !== -1) return 1;
        return a.order - b.order;
      }),
  };
}

export function validateProjectMetadataSchema(input: unknown): string | null {
  if (!input || typeof input !== "object") {
    return "metadata_schema must be an object";
  }

  const schema = input as Partial<ProjectMetadataSchema>;

  if (schema.version !== 1) {
    return "metadata_schema.version must be 1";
  }

  if (typeof schema.showEmptyFields !== "boolean") {
    return "metadata_schema.showEmptyFields must be a boolean";
  }

  if (schema.standard !== undefined && !METADATA_STANDARDS.includes(schema.standard)) {
    return "metadata_schema.standard is invalid";
  }

  if (!Array.isArray(schema.fields) || schema.fields.length === 0) {
    return "metadata_schema.fields must be a non-empty array";
  }

  const seenKeys = new Set<string>();
  const validAnswerTypes = [
    "Text",
    "Numeric",
    "Date",
    "Multi-Select",
    "Dropdown List",
    "Radio Button",
    "Descriptive Text",
  ];

  for (let index = 0; index < schema.fields.length; index++) {
    const field = schema.fields[index];
    const fieldPath = `metadata_schema.fields[${index}]`;

    if (!field || typeof field !== "object") {
      return `${fieldPath} must be an object`;
    }

    if (typeof field.key !== "string" || field.key.trim() === "") {
      return `${fieldPath}.key must be a non-empty string`;
    }

    const key = field.key.trim();

    if (seenKeys.has(key)) {
      return `duplicate metadata field key: ${key}`;
    }
    seenKeys.add(key);

    if (typeof field.label !== "string" || field.label.trim() === "") {
      return `${fieldPath}.label must be a non-empty string`;
    }

    if (typeof field.enabled !== "boolean") {
      return `${fieldPath}.enabled must be a boolean`;
    }

    if (typeof field.order !== "number" || !Number.isFinite(field.order)) {
      return `${fieldPath}.order must be a number`;
    }

    if (field.source !== "catalog" && field.source !== "custom") {
      return `${fieldPath}.source must be catalog or custom`;
    }

    if (field.source === "catalog") {
      if (!KNOWN_METADATA_FIELD_KEY_SET.has(key)) {
        return `${fieldPath}.key is not a catalog metadata field`;
      }
    } else if (KNOWN_METADATA_FIELD_KEY_SET.has(key) || !CUSTOM_METADATA_FIELD_KEY_PATTERN.test(key)) {
      return `${fieldPath}.key must be a unique custom field key (lowercase letters, numbers, underscores)`;
    }

    if (!validAnswerTypes.includes(field.answerType)) {
      return `${fieldPath}.answerType is invalid`;
    }

    if (typeof field.required !== "boolean") {
      return `${fieldPath}.required must be a boolean`;
    }

    if (field.options != null && (!Array.isArray(field.options) || field.options.some((o) => typeof o !== "string"))) {
      return `${fieldPath}.options must be an array of strings`;
    }

    if (field.description !== undefined && field.description !== null && typeof field.description !== "string") {
      return `${fieldPath}.description must be a string`;
    }

    if (field.deleted !== undefined && field.deleted !== null && typeof field.deleted !== "boolean") {
      return `${fieldPath}.deleted must be a boolean`;
    }
  }

  const requiredBaseKeys = [...LOCKED_BASE_METADATA_FIELD_KEYS];
  for (const baseKey of requiredBaseKeys) {
    if (!seenKeys.has(baseKey)) {
      return `missing foundational metadata field: ${baseKey}`;
    }
  }

  return null;
}

export type BulkImportColumnType = "story_id" | "context" | "project_metadata" | "prep_session_metadata";

export interface BulkImportColumnDef {
  header: string;
  type: BulkImportColumnType;
  key: string;
  label: string;
  answerType?: MetadataAnswerType;
  options?: string[];
  required?: boolean;
  prepSessionId?: string;
  prepSessionTitle?: string;
}

export interface BulkImportRowValidation {
  rowNumber: number;
  storyId: string;
  storyTitle?: string;
  sessionType?: string;
  values: Record<string, string>;
  errors: Record<string, string>;
  isValid: boolean;
}

export interface BulkImportValidationResult {
  isValid: boolean;
  totalRows: number;
  errorCount: number;
  errorRowsCount: number;
  headers: string[];
  rows: BulkImportRowValidation[];
  headerErrors?: string[];
}

export interface BulkImportCommitRow {
  storyId: string;
  values: Record<string, string>;
}

export interface BulkImportCommitPayload {
  rows: BulkImportCommitRow[];
}

export interface BulkImportCommitResponse {
  success: boolean;
  updatedStoriesCount: number;
  message?: string;
}
